import React, { FC, useState, useEffect } from 'react';
import { Form, Input, Select, Button, Modal, notification } from 'antd';
import { PropertyType } from '../../../shared/models/propertyType.enum';
import styles from './FindingTypePropertyTemplate.module.scss';
import { connect } from 'react-redux';
import { AppDispatch } from '../../../store';
import { FindingTypePropertyTemplateType, saveFindingTypePropertyTemplate, showModal } from './findingTypePropertyTemplate.slice';
import ValuesTable, { DataType } from './EditableTable';

type FindingTypePropertyTemplateProps = {
  loading: boolean;
  onFindingTypePropertyTemplateSave: (findingTypePropertyTemplate: FindingTypePropertyTemplateType) => void;
  onShowModal: (visible: boolean) => void;
  findingTypePropertyTemplate: FindingTypePropertyTemplateType | undefined;
};

type IdValue = {
  id: string;
  value: string;
};

const propertyUnits: IdValue[] = [
  { id: '1', value: 'm' },
  { id: '2', value: 'mm' },
  { id: '3', value: 'm2' },
  { id: '4', value: 'mm2' },
  { id: '5', value: 'px/mm' },
  { id: '6', value: 'px/m' }
];

const FindingTypePropertyTemplate: FC<FindingTypePropertyTemplateProps> = ({
  onFindingTypePropertyTemplateSave,
  onShowModal,
  findingTypePropertyTemplate
}) => {
  const [description, setDescription] = useState('');
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [count, setCount] = useState(0);
  const [units, setUnits] = useState('');
  const [value, setValue] = useState('');
  const [id, setId] = useState('');
  const { Option } = Select;

  const [form] = Form.useForm();

  useEffect(() => {
    if (findingTypePropertyTemplate) {
      form.setFieldsValue({
        fullName: findingTypePropertyTemplate.fullName,
        shortName: findingTypePropertyTemplate.shortName
      });
      setId(findingTypePropertyTemplate.id!);
      setDescription(findingTypePropertyTemplate.description);
      setUnits(findingTypePropertyTemplate.units);
      if (findingTypePropertyTemplate.values !== null && findingTypePropertyTemplate.values !== '') {
        setDataSource(
          findingTypePropertyTemplate.values.split(',').map((x, index) => {
            return { key: index.toString(), value: x };
          })
        );
        setCount(findingTypePropertyTemplate.values.split(',').length);
      }
    }
  }, [findingTypePropertyTemplate]);
  const handleSubmit = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    try {
      var result = await form.validateFields();
      const newItem: FindingTypePropertyTemplateType = {
        id: id,
        fullName: form.getFieldValue('fullName'),
        shortName: form.getFieldValue('shortName'),
        description: description,
        type: form.getFieldValue('type'),
        units: units,
        values: dataSource.map(x => x.value).join(','),
        propertyId: null,
        canDelete: false
      };
      onFindingTypePropertyTemplateSave(newItem);
      onShowModal(false);
    } catch (error) {}
  };

  const formItemLayout = {
    labelCol: {
      xs: { span: 8 },
      sm: { span: 8 }
    },
    wrapperCol: {
      xs: { span: 16 },
      sm: { span: 16 }
    }
  };

  const types = [];
  for (const type in PropertyType) {
    if (typeof PropertyType[type] === 'number') {
      types.push(<Option value={type}>{type}</Option>);
    }
  }

  const handleAdd = () => {
    if (value === '') {
      return;
    }
    if (dataSource.find(x => x.value === value)) {
      notification.error({ message: 'Value already exists' });
      return;
    }
    if (value.includes(',')) {
      notification.error({ message: 'Value cannot contain comma' });
      return;
    }
    const newData: DataType = {
      key: count,
      value
    };
    setDataSource([...dataSource, newData]);
    setValue('');
    setCount(count + 1);
  };

  return (
    <Modal
      title={findingTypePropertyTemplate ? 'Update property template' : 'Add new property template'}
      visible={true}
      onCancel={() => onShowModal(false)}
      footer={[
        <Button key='submit' htmlType='submit' onClick={e => handleSubmit(e)}>
          Save
        </Button>,
        <Button key='back' onClick={() => onShowModal(false)}>
          Cancel
        </Button>
      ]}
    >
      <Form {...formItemLayout} layout='vertical' form={form}>
        <Form.Item
          name='fullName'
          label='Property name'
          className={styles.antFormItem}
          rules={[{ required: true, message: 'Please enter property name.', whitespace: true }]}
        >
          <Input placeholder='Enter Property name' />
        </Form.Item>
        <Form.Item
          name='shortName'
          label='Property short name'
          className={styles.antFormItem}
          rules={[{ required: true, message: 'Please enter property name.', whitespace: true }]}
        >
          <Input placeholder='Enter Property short name' />
        </Form.Item>
        <Form.Item label='Description' className={styles.antFormItem}>
          <Input.TextArea
            placeholder='Enter Property description'
            value={description}
            onChange={e => {
              setDescription(e.target.value);
            }}
          />
        </Form.Item>
        <Form.Item
          name='type'
          label='Data type'
          className={styles.antFormItem}
          initialValue={findingTypePropertyTemplate ? PropertyType[findingTypePropertyTemplate.type!] : ''}
          rules={[{ required: true, message: 'Please select data type' }]}
        >
          <Select placeholder='Select data type'>{types}</Select>
        </Form.Item>
        <Form.Item label='Unit' className={styles.antFormItem}>
          <Select
            allowClear={true}
            placeholder='Please select unit'
            onChange={e => {
              if (e == undefined) {
                setUnits('');
                return;
              }
              setUnits(propertyUnits.find(x => x.id === e)!.value);
            }}
            filterOption={false}
            value={units}
          >
            {propertyUnits.map(item => (
              <Select.Option value={item.id} key={item.value}>
                {item.value}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label='Value(s)' className={styles.antFormItem}>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Input
              placeholder='Enter Property value(s)'
              value={value}
              onChange={e => {
                setValue(e.target.value);
              }}
            />
            <Button onClick={handleAdd}>Add</Button>
          </div>
        </Form.Item>
        <ValuesTable dataSource={dataSource} setDataSource={setDataSource} />
      </Form>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onFindingTypePropertyTemplateSave: (findingTypePropertyTemplate: FindingTypePropertyTemplateType) =>
    dispatch(saveFindingTypePropertyTemplate(findingTypePropertyTemplate)),
  onShowModal: (value: boolean) => dispatch(showModal(value))
});

export default connect(null, mapDispatchToProps)(FindingTypePropertyTemplate);
